import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BASE_URL, POST_DOCTOR, GET_DEPARTMENTS } from '../utlilities/config';
import { FiPlus, FiUser, FiBriefcase, FiAward, FiCheck, FiX } from 'react-icons/fi';
import AddDoctorDepartment from './AddDoctorDepartment';
import Select from 'react-select';

// Animated loader component
const Spinner = () => (
  <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Processing...</p>
    </div>
  </div>
);

// Custom Select styles - improved for modern look
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minWidth: '200px',
    height: '52px',
    backgroundColor: '#f9fafb',
    boxShadow: state && state.isFocused ? '0 0 0 2px #4f46e5' : 'none',
    borderColor: state && state.isFocused ? '#4f46e5' : provided.borderColor,
    borderRadius: '0.5rem',
    paddingLeft: '8px',
    '&:hover': {
      borderColor: '#4f46e5',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state && state.isSelected
      ? 'rgba(79, 70, 229, 0.1)'
      : state && state.isFocused
      ? 'rgba(79, 70, 229, 0.05)'
      : '#ffffff',
    color: state && state.isSelected ? '#4f46e5' : state && state.isFocused ? '#4f46e5' : '#374151',
    fontWeight: state && state.isSelected ? '500' : '400',
    '&:hover': {
      backgroundColor: 'rgba(79, 70, 229, 0.05)',
      color: '#4f46e5',
    },
    padding: '10px 16px',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    position: 'absolute',
    borderRadius: '0.5rem',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 8px',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9CA3AF',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#374151',
  }),
};

const AddDoctorModal = ({ setDoctorModal, refreshData, onDoctorSaved, doctorToEdit }) => {
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('accessToken');
  const [departmentModal, setDepartmentModal] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const isEditing = Boolean(doctorToEdit);

  useEffect(() => {
    getDepartments();
  }, []);

  const getDepartments = async () => {
    try {
      const response = await axios.get(`${BASE_URL}${GET_DEPARTMENTS}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.status === 200) {
        setDepartments(response.data.data);
      }
    } catch (error) {
      console.log('Error fetching departments');
    }
  };

  const handleNewDepartmentSelected = (newDepartment) => {
    const departmentOption = { value: newDepartment.DepartmentName, label: newDepartment.DepartmentName };
    setSelectedDepartment(departmentOption);
    formik.setFieldValue('department', departmentOption.value);
    getDepartments();
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      designation: '',
      department: '',
      ID: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Doctor Name is required'),
      designation: Yup.string(),
      department: Yup.string(),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        if (isEditing) {
          // Send PUT request to update doctor

          const apiData = {
            ID: values.ID,
            name: `Dr ${values.name}`,
            department: values.department,
            designation: values.designation,
            mobileNo: doctorToEdit?.mobileNo,
            address: doctorToEdit?.address,
            personalDetails: doctorToEdit?.personalDetails,
            imageUrl: doctorToEdit?.imageUrl,
            slotDuration: doctorToEdit?.slotDuration,
            weeklySchedule: doctorToEdit?.weeklySchedule,
            newRegistrationFee: doctorToEdit?.newRegistrationFee,
            renewRegistrationFee : doctorToEdit?.renewRegistrationFee,
            newConsultFee : doctorToEdit?.newConsultFee,
            renewConsultFee : doctorToEdit?.renewConsultFee,
            validityDays : doctorToEdit?.validityDays,
            cutsAmount : doctorToEdit?.cutsAmount,
            commission : doctorToEdit?.commission,
            avgAmount: doctorToEdit?.avgAmount,
          };

          const response = await axios.post(`${BASE_URL}${POST_DOCTOR}`, apiData,  {
            headers: {
              'XApiKey': process.env.REACT_APP_API_KEY,
              'Authorization': `Bearer ${token}`,
            },
          });
          if (response.status === 200) {
            setLoading(false);
            const newDoctor = { DoctorName: `Dr ${values.name}`, Designation: values.designation, Department: values.department };
            onDoctorSaved(newDoctor);
            setDoctorModal(false);
            refreshData();
          }
        } else {

          const apiData = {
            ID: null,
            name: `Dr ${values.name}`,
            department: values.department,
            designation: values.designation,
            mobileNo: '',
            address: '',
            personalDetails: '',
            imageUrl: '',
            slotDuration: 0,
            weeklySchedule: [],
            newRegistrationFee: 0,
            renewRegistrationFee : 0,
            newConsultFee : 0,
            renewConsultFee : 0,
            validityDays : 0,
            cutsAmount : 0,
            commission : 0,
            avgAmount: 0,
          };


          const response = await axios.post(`${BASE_URL}${POST_DOCTOR}`, apiData, {
            headers: {
              'XApiKey': process.env.REACT_APP_API_KEY,
              'Authorization': `Bearer ${token}`,
            },
          });
          if (response.status === 200) {
            setDoctorModal(false);
            refreshData();
            const newDoctor = response.data.data[0];
            if (onDoctorSaved) {
              onDoctorSaved(newDoctor);
            }
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (doctorToEdit) {
      formik.setValues({
        name: doctorToEdit.doctorName?.replace(/^Dr\s+/i, '') || '',
        designation: doctorToEdit.designation || '',
        department: doctorToEdit.department || '',
        ID: doctorToEdit.doctorID || '',
      });

      const departmentOption = departments_options.find(option => option.value === doctorToEdit.Department);
      setSelectedDepartment(departmentOption || null);
    } else {
      formik.resetForm();
    }
  }, [doctorToEdit]);

  const departments_options = departments.map(item => ({
    value: item.DepartmentName,
    label: item.DepartmentName,
  }));

  const nameref = useRef(null);
  const departmentref = useRef(null);
  const designationref = useRef(null);
  const btn = useRef(null);

  useEffect(() => {
    nameref.current?.focus();
  }, []);

  const handleKeyDown = (e, nextFieldRef, fallbackRef = null) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextFieldRef?.current) {
        nextFieldRef.current.focus();
      } else if (fallbackRef?.current) {
        fallbackRef.current.focus();
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      {loading && <Spinner />}
      
      <div className="bg-white w-3/5 rounded-2xl shadow-2xl transform transition-transform duration-300 ease-out animate-zoom-in overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-5 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <FiUser className="mr-2" /> {isEditing ? 'Edit Doctor' : 'Add New Doctor'}
          </h2>
          <button 
            onClick={() => setDoctorModal(false)}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors duration-200"
            aria-label="Close modal"
          >
            <FiX size={20} />
          </button>
        </div>
        
        <form onSubmit={formik.handleSubmit} className="p-8 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Doctor Name Field */}
            <div className="w-full">
              <div className="flex justify-between mb-1.5">
                <label className="text-sm font-medium text-gray-700 flex items-center">
                  Doctor Name <span className="text-red-500 ml-1">*</span>
                </label>
              </div>
              <div className="flex w-full gap-3 items-stretch">
                <div className="bg-gray-50 py-3 px-4 border border-gray-200 rounded-lg text-gray-600 font-medium flex items-center">
                  DR.
                </div>
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                    <FiUser />
                  </div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter Doctor Name"
                    className={`w-full pl-10 pr-3 py-3 border ${
                      formik.touched.name && formik.errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                    } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                    value={formik.values.name}
                    onChange={(e) => {
                      const value = e.target.value || '';
                      formik.setFieldValue('name', value.toUpperCase());
                    }}
                    onBlur={formik.handleBlur}
                    ref={nameref}
                    onKeyDown={(e) => handleKeyDown(e, departmentref)}
                  />
                </div>
              </div>
              {formik.touched.name && formik.errors.name && (
                <p className="mt-1 text-sm text-red-500">{formik.errors.name}</p>
              )}
            </div>

            {/* Department Field */}
            <div className="w-full">
              <div className="flex justify-between mb-1.5">
                <label className="text-sm font-medium text-gray-700 flex items-center">
                  Doctor Department <span className="text-red-500 ml-1">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setDepartmentModal(true)}
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center transition-colors"
                >
                  <FiPlus className="mr-1" size={14} /> Add New
                </button>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                  <FiBriefcase />
                </div>
                <Select
                  options={departments_options}
                  placeholder="Select Department"
                  className="pl-8"
                  isSearchable
                  name="department"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  value={selectedDepartment || departments_options.find(option => option.value === formik.values.department)}
                  onChange={(selectedOption) => {
                    setSelectedDepartment(selectedOption);
                    formik.setFieldValue('department', selectedOption ? selectedOption.value : '');
                  }}
                  styles={{
                    ...customSelectStyles,
                    control: (base, state) => ({
                      ...customSelectStyles.control(base, state),
                      paddingLeft: '28px' // Adjust for the icon
                    })
                  }}
                  ref={departmentref}
                  onMenuClose={() => designationref.current?.focus()}
                />
              </div>
            </div>
          </div>
          
          {/* Designation Field */}
          <div className="w-full">
            <div className="flex justify-between mb-1.5">
              <label className="text-sm font-medium text-gray-700 flex items-center">
                Doctor Designation <span className="text-red-500 ml-1">*</span>
              </label>
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                <FiAward />
              </div>
              <input
                type="text"
                name="designation"
                placeholder="Enter Doctor Designation"
                className={`w-full pl-10 pr-3 py-3 border ${
                  formik.touched.designation && formik.errors.designation ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                value={formik.values.designation}
                onChange={(e) => {
                  const value = e.target.value || '';
                  formik.setFieldValue('designation', value.toUpperCase());
                }}
                onBlur={formik.handleBlur}
                ref={designationref}
                onKeyDown={(e) => handleKeyDown(e, btn)}
              />
            </div>
            {formik.touched.designation && formik.errors.designation && (
              <p className="mt-1 text-sm text-red-500">{formik.errors.designation}</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-100">
            <button
              type="button"
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:ring-gray-200 transition-colors duration-200 flex items-center"
              onClick={() => setDoctorModal(false)}
            >
              <FiX className="mr-2" /> Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:ring-4 focus:ring-indigo-300 transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
              ref={btn}
            >
              <FiCheck className="mr-2" /> {isEditing ? 'Update Doctor' : 'Save Doctor'}
            </button>
          </div>
        </form>

        {departmentModal && (
          <AddDoctorDepartment 
            setDepartmentModal={setDepartmentModal} 
            refreshData={getDepartments} 
            onDepartmentSaved={handleNewDepartmentSelected} 
          />
        )}
      </div>
    </div>
  );
};

export default AddDoctorModal;